import {ActionIcon, Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useEffect, useMemo} from "react";
import {
  ActivityPeriodSearchCriteria,
  activityPeriodsSelector,
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  startOfDay,
  subDays,
  useSearchOperatorActivityPeriodsQuery,
} from "@ots-frontend/ots-data";
import {IconChevronDown, IconFileBarcode, IconTruck} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
  ActivityPeriodDataTable,
  FromToDateToolbar,
  PAGINATION_PAGE_SIZES,
  ResizeableContent,
  selectUIActivityPeriodSearchFilter,
  setUIActivityPeriodSearchFilter,
  ThemedPage,
  Tooltip,
  useApp,
} from "@ots-frontend/ots-ui";
import {useDispatch} from "react-redux";
import {skipToken} from "@reduxjs/toolkit/query";
import {useAppSelector} from "../../store/hooks/useAppSelector";

export type TruckingPageProps = {}

export const TruckingPage = (props: TruckingPageProps) => {

  const dispatch = useDispatch();
  const navigate = useNavigate()
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const criteria = useAppSelector(selectUIActivityPeriodSearchFilter)

  const selectActivityPeriods = useMemo(() => {
    return activityPeriodsSelector();
  }, []);

  const searchArg = useMemo(() => {
    console.log("searchArg")
    if (regionId && criteria?.fromDttm) {
      return {
        ...criteria,
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        regionId: regionId,
        category: appCategory?.code,
      }
    } else {
      return skipToken
    }
  }, [criteria, regionId, appCategory])

  const {activityPeriods, isFetching, refetch} = useSearchOperatorActivityPeriodsQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      activityPeriods: selectActivityPeriods(result, {regionId}),
      isFetching: result.isFetching,
    })
  });

  useEffect(() => {
    if (!criteria || !criteria.fromDttm) {
      dispatch(
        setUIActivityPeriodSearchFilter({
          fromDttm: startOfDay(subDays(new Date(), 7)).getTime(),
        })
      )
    }
  }, [criteria]);

  const onFilterChange = useCallback((fromDate: Date, toDate: Date | undefined) => {

    const newCriteria: ActivityPeriodSearchCriteria = {
      fromDttm: fromDate.getTime(),
      toDttm: toDate?.getTime(),
    }

    let criteriaChange = false
    if (criteria?.fromDttm !== newCriteria.fromDttm) {
      criteriaChange = true
    }
    if (criteria?.toDttm !== newCriteria.toDttm) {
      criteriaChange = true
    }

    if (!criteriaChange) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setUIActivityPeriodSearchFilter({
        ...newCriteria,
      }))
    }
  }, [criteria, refetch, dispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconTruck/>
                      <Title order={4}>
                        {t("page.trucking.title", "Trucking Activity Periods", {ns: "translation"})}
                      </Title>
                      <Tooltip
                        label={t("common.entity.transporttransportassoc.plural", "Trucking Tickets")}
                        withinPortal={true}>
                        <ActionIcon
                          variant="subtle"
                          onClick={() => navigate("tickets")}>
                          <IconFileBarcode size="1.125rem"/>
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t("common.action.plural", "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <FromToDateToolbar
                    defaultValues={{
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : startOfDay(subDays(new Date(), 7)),
                      toDate: criteria?.toDttm ? new Date(criteria.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    onSubmit={onFilterChange}/>
                </Card>
                <ActivityPeriodDataTable
                  withBorder
                  records={activityPeriods}
                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
